import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ComposedChart, Area, Scatter, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, ResponsiveContainer } from "recharts";
import { Plus, Trash2, Coins } from "lucide-react";
import { api } from "../lib/api";

const LINE_COLORS = ["#b3572f", "#71416b", "#2f6f8f", "#8a7a2f"];

export default function IbjaChart() {
  const [ibja, setIbja] = useState(null);
  const [myPrices, setMyPrices] = useState([]);
  const [proj, setProj] = useState(null);
  const [metal, setMetal] = useState("gold");
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), price: "" });
  const [saving, setSaving] = useState(false);

  const loadMyPrices = () => api.get("/metals/my-prices").then((r) => setMyPrices(r.data.prices || [])).catch(() => {});

  useEffect(() => {
    api.get("/metals/ibja", { timeout: 60000 }).then((r) => setIbja(r.data)).catch(() => {});
    api.get("/metals/projections", { timeout: 120000 }).then((r) => setProj(r.data)).catch(() => {});
    loadMyPrices();
  }, []);

  const { data, refLines, domain } = useMemo(() => {
    if (!ibja) return { data: [], refLines: [], domain: ["auto", "auto"] };
    const series = metal === "gold"
      ? ibja.gold.dates.map((d, i) => ({ date: d, ibja: ibja.gold.k24_per_10g[i] }))
      : ibja.silver.dates.map((d, i) => ({ date: d, ibja: ibja.silver.per_kg[i] }));
    const mine = myPrices.filter((p) => p.metal === metal);
    const byDate = Object.fromEntries(series.map((s) => [s.date, s]));
    mine.forEach((p) => {
      if (byDate[p.date]) byDate[p.date].mine = p.price;
      else series.push({ date: p.date, mine: p.price });
    });
    series.sort((a, b) => (a.date < b.date ? -1 : 1));
    const lines = (proj?.[metal] || []).map((f, i) => ({
      firm: f.firm,
      year: f.year,
      value: metal === "gold" ? f.target_inr_per_10g : f.target_inr_per_kg,
      color: LINE_COLORS[i % LINE_COLORS.length],
    })).filter((l) => l.value);
    const vals = [
      ...series.map((s) => s.ibja).filter(Boolean),
      ...series.map((s) => s.mine).filter(Boolean),
      ...lines.map((l) => l.value),
    ];
    const dom = vals.length ? [Math.floor(Math.min(...vals) * 0.97), Math.ceil(Math.max(...vals) * 1.03)] : ["auto", "auto"];
    return { data: series, refLines: lines, domain: dom };
  }, [ibja, myPrices, proj, metal]);

  const addPrice = async (e) => {
    e.preventDefault();
    const price = parseFloat(form.price);
    if (!price || saving) return;
    setSaving(true);
    try {
      await api.post("/metals/my-prices", { metal, date: form.date, price });
      toast.success("Your price added to the chart");
      setForm((f) => ({ ...f, price: "" }));
      loadMyPrices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const removePrice = async (id) => {
    try {
      await api.delete(`/metals/my-prices/${id}`);
      loadMyPrices();
    } catch {
      toast.error("Could not delete");
    }
  };

  const minePoints = myPrices.filter((p) => p.metal === metal);
  const unit = metal === "gold" ? "₹/10g (24K)" : "₹/kg";
  const latest = metal === "gold" ? ibja?.gold?.latest_k24 : ibja?.silver?.latest;

  return (
    <div className="bg-white border border-line rounded-sm p-5" data-testid="ibja-chart">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <div>
          <div className="text-[0.65rem] font-mono uppercase tracking-widest text-moss">
            Official All-India Bullion (IBJA) · {unit}
          </div>
          {latest && (
            <div className="font-mono text-lg text-stone_brand-dark" data-testid="ibja-latest">
              ₹{Number(latest).toLocaleString("en-IN")} <span className="text-[0.6rem] text-stone_brand-light">{unit} · {metal === "gold" ? ibja?.gold?.latest_date : ibja?.silver?.latest_date}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {[
            { key: "gold", label: "Gold 24K", color: "text-amber_brand" },
            { key: "silver", label: "Silver", color: "text-slate-500" },
          ].map((m) => (
            <button
              key={m.key}
              onClick={() => setMetal(m.key)}
              className={`px-3 py-1 rounded-sm text-xs font-mono tracking-wider transition-colors inline-flex items-center gap-1.5 ${
                metal === m.key ? "bg-stone_brand-dark text-bone-50" : "bg-white border border-line text-stone_brand hover:border-moss/50"
              }`}
              data-testid={`ibja-metal-${m.key}`}
            >
              <Coins className={`w-3 h-3 ${metal === m.key ? "" : m.color}`} strokeWidth={2} />
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {!ibja ? (
        <div className="h-60 bg-bone-100 rounded-sm animate-pulse" />
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="ibjaFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3f5c40" stopOpacity={0.22} />
                <stop offset="100%" stopColor="#3f5c40" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e7e2d9" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={40} />
            <YAxis tick={{ fontSize: 10 }} domain={domain} width={62} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
            <Tooltip formatter={(v, name) => [`₹${Number(v).toLocaleString("en-IN")}`, name === "ibja" ? "IBJA official" : "Your price"]} labelStyle={{ fontSize: 11 }} />
            {refLines.map((l) => (
              <ReferenceLine
                key={l.firm}
                y={l.value}
                stroke={l.color}
                strokeDasharray="6 4"
                label={{ value: `${l.firm} ${l.year || ""} → ₹${Math.round(l.value / 1000)}k`, fontSize: 9, fill: l.color, position: "insideTopRight" }}
              />
            ))}
            <Area type="monotone" dataKey="ibja" stroke="#3f5c40" strokeWidth={1.6} fill="url(#ibjaFill)" name="ibja" />
            <Scatter dataKey="mine" fill="#b3572f" name="mine" />
          </ComposedChart>
        </ResponsiveContainer>
      )}

      <form onSubmit={addPrice} className="flex flex-wrap items-center gap-2 mt-3" data-testid="my-price-form">
        <span className="text-[0.65rem] font-mono uppercase tracking-widest text-stone_brand-light">Add your {metal} price ({unit}):</span>
        <input
          type="date"
          value={form.date}
          onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
          className="h-9 px-2 text-xs font-mono rounded-sm border border-line bg-white focus:outline-none focus:ring-1 focus:ring-moss"
          data-testid="my-price-date"
        />
        <input
          type="number"
          step="any"
          placeholder={metal === "gold" ? "e.g. 142500" : "e.g. 216000"}
          value={form.price}
          onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
          className="h-9 px-2 text-xs font-mono rounded-sm border border-line bg-white w-28 focus:outline-none focus:ring-1 focus:ring-moss"
          data-testid="my-price-input"
        />
        <button type="submit" disabled={saving || !form.price} className="h-9 px-3 rounded-sm bg-moss text-bone-50 text-xs font-mono uppercase tracking-wider hover:bg-moss-hover disabled:opacity-50 flex items-center gap-1" data-testid="my-price-add-btn">
          <Plus className="w-3.5 h-3.5" /> Plot it
        </button>
      </form>

      {minePoints.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="my-price-list">
          {minePoints.map((p) => (
            <span key={p.id} className="inline-flex items-center gap-1 text-[0.7rem] font-mono bg-terracotta-bg text-stone_brand-dark border border-terracotta/30 px-2 py-0.5 rounded-sm">
              {p.date} · ₹{Number(p.price).toLocaleString("en-IN")}
              <button onClick={() => removePrice(p.id)} className="text-terracotta hover:opacity-70" data-testid={`my-price-del-${p.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <p className="text-[0.6rem] font-mono uppercase tracking-widest text-stone_brand-light mt-2">
        — Dashed lines = expert house targets (AI-curated) · dots = your prices · solid = official IBJA. If a house's line hugs the real curve, that expert is tracking reality.
      </p>
    </div>
  );
}
